/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import UserTypeUtil, { USERTYPE } from '@cchome/home-components/src/components/PersonalizedHome/UserTypeUtil';

/* eslint-enable no-unused-vars */
import { connect } from 'react-redux';
import { setForceHideLanguageSwitcher } from '../../actions/appActions';
import getConfig from '../../../config';
import open from '../../../utils/open';
import './Footer.scss';

interface Props {
	locale: string;
	intl: any;
	forceHideLanguageSwitcher: boolean;
	hideLanguageSwitcher: boolean;
	isCCD: boolean;
}

class Footer extends Component<Props> {
	onClickCookiePref() {
		if (window.adobePrivacy && window.adobePrivacy.showConsentPopup) {
			window.adobePrivacy.showConsentPopup();
		}
	}

	handleOnKeyDown(e) {
		if (e.key === ' ' || e.key === 'Enter' || e.which === 13 || e.which === 32) {
			e.currentTarget.click();
		}
	}

	isHomeV3() {
		if (
			window.cchome.util.hasFeature('home_version_3') &&
			window.cchome.util.hasFeature('home_version_3_test') &&
			!window.cchome.util.isIE() &&
			// !window.cchome.util.isEdge() &&
			this.props.locale === 'en' &&
			UserTypeUtil.isValidV3UserType()
		) {
			return true;
		}
		return false;
	}

	render() {
		const baseURL = getConfig('ADOBE_BASE_GOURL');

		return (
			<div
				id="footer"
				aria-label={this.props.intl.formatMessage({ id: 'app.footer.aria.label' })}
				role="contentinfo"
				className={`footer-container ${this.isHomeV3() ? 'v3' : ''}`}
			>
				<ul className="footer-left">
					{this.props.isCCD && (
						<li>
							<div>
								<span
									onClick={() => open('http://www.adobe.com/go/cc_customersupport')}
									tabIndex={0}
									onKeyDown={this.handleOnKeyDown}
								>
									<FormattedMessage id="app.footer.help" />
								</span>
							</div>
						</li>
					)}
					<li>
						<div>
							<span
								onClick={() =>
									open(`${baseURL}${this.props.intl.formatMessage({ id: 'app.footer.tou.url' })}`)
								}
								tabIndex={0}
								onKeyDown={this.handleOnKeyDown}
							>
								<FormattedMessage id="app.footer.tou" />
							</span>
						</div>
					</li>
					<li>
						<div>
							<span
								onClick={() =>
									open(
										`${baseURL}${this.props.intl.formatMessage({
											id: 'app.footer.privacyPolicy.url',
										})}`,
									)
								}
								tabIndex={0}
								onKeyDown={this.handleOnKeyDown}
							>
								<FormattedMessage id="app.footer.privacyPolicy" />
							</span>
						</div>
					</li>
					<li>
						<div>
							<span
								onClick={() =>
									open(`${baseURL}${this.props.intl.formatMessage({ id: 'app.footer.cookies.url' })}`)
								}
								tabIndex={0}
								onKeyDown={this.handleOnKeyDown}
							>
								<FormattedMessage id="app.footer.cookies" />
							</span>
						</div>
					</li>
					<li>
						<div className="evidon-notice-link consent-link">
							<span
								onClick={() => this.onClickCookiePref()}
								tabIndex={0}
								onKeyDown={this.handleOnKeyDown}
							>
								<FormattedMessage tagName="span" id="app.footer.cookiePreferences" />
							</span>
							<div className="consent-link-hidden"></div>
						</div>
					</li>
					{!(this.props.forceHideLanguageSwitcher || this.props.hideLanguageSwitcher) ? (
						<li className="footer-languageSwitcher-xs">
							<LanguageSwitcher currentLocale={this.props.locale} />
						</li>
					) : null}
				</ul>
				{!(this.props.forceHideLanguageSwitcher || this.props.hideLanguageSwitcher) ? (
					<ul className="footer-right">
						<li className="footer-languageSwitcher">
							<LanguageSwitcher currentLocale={this.props.locale} />
						</li>
						<li className="footer-copyright">
							&copy;
							<FormattedMessage id="app.footer.copyright" />
						</li>
					</ul>
				) : (
					<ul className="footer-right ff-switcher">
						<li className="footer-copyright">
							&copy;
							<FormattedMessage id="app.footer.copyright" />
						</li>
					</ul>
				)}
			</div>
		);
	}
}

function mapStateToProps(state) {
	return {
		forceHideLanguageSwitcher: state.app.forceHideLanguageSwitcher,
	};
}

export default connect(mapStateToProps, { setForceHideLanguageSwitcher })(injectIntl(Footer));
